import { SEARCH_V2_ANALYTICS_METADATA } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { SearchV2Filters, SearchV2Weights } from "@/types";

// One event per changed field so `sort_filter_name` / `option_selected` are
// chartable on their own; every event also carries the full post-change state.
type SearchV2FilterSource = "manual" | "nl_classifier" | "recent_search";

const getKeywordList = (filters: SearchV2Filters) =>
  filters.keywords.map(({ keyword }) => keyword);

const getActiveFilterCount = (filters: SearchV2Filters) =>
  Number(filters.keywords.length > 0) +
  Number(filters.title.length > 0) +
  Number(Boolean(filters.payCeiling)) +
  Number(Boolean(filters.location)) +
  Number(filters.includeRejected) +
  Number(filters.includePastSubmitted);

const getFilterStateMetadata = (
  filters: SearchV2Filters,
  jobId: string,
  source: SearchV2FilterSource,
) => ({
  ...SEARCH_V2_ANALYTICS_METADATA,
  active_filter_count: getActiveFilterCount(filters),
  include_past_submitted: filters.includePastSubmitted,
  include_rejected: filters.includeRejected,
  job_id: jobId,
  keyword_count: filters.keywords.length,
  keywords: getKeywordList(filters),
  location: filters.location,
  pay_ceiling: filters.payCeiling,
  source,
  title: filters.title,
});

const areStringListsEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

export const searchV2Analytics = {
  trackFilterChanges: ({
    jobId,
    nextFilters,
    previousFilters,
    source,
  }: {
    jobId: string;
    nextFilters: SearchV2Filters;
    previousFilters: SearchV2Filters;
    source: SearchV2FilterSource;
  }) => {
    const changes: { name: string; value: string | boolean | unknown[] }[] = [];
    if (
      !areStringListsEqual(
        getKeywordList(previousFilters),
        getKeywordList(nextFilters),
      )
    ) {
      changes.push({ name: "Keywords", value: getKeywordList(nextFilters) });
    }
    if (!areStringListsEqual(previousFilters.title, nextFilters.title)) {
      changes.push({ name: "Title", value: nextFilters.title });
    }
    if (previousFilters.payCeiling !== nextFilters.payCeiling) {
      changes.push({ name: "Pay ceiling", value: nextFilters.payCeiling });
    }
    if (previousFilters.location !== nextFilters.location) {
      changes.push({ name: "Location", value: nextFilters.location ?? "" });
    }
    if (previousFilters.includeRejected !== nextFilters.includeRejected) {
      changes.push({
        name: "Include rejected",
        value: nextFilters.includeRejected,
      });
    }
    if (
      previousFilters.includePastSubmitted !== nextFilters.includePastSubmitted
    ) {
      changes.push({
        name: "Include past submitted",
        value: nextFilters.includePastSubmitted,
      });
    }

    const metadata = getFilterStateMetadata(nextFilters, jobId, source);
    changes.forEach(({ name, value }) => {
      mixpanelUtils.trackFilterAndSortOptionSelected(
        MIXPANEL_SCREENS.JOB,
        name,
        value,
        metadata,
      );
    });
  },

  trackRadiusChange: ({ jobId, radius }: { jobId: string; radius: string }) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Distance radius",
      radius,
      { ...SEARCH_V2_ANALYTICS_METADATA, job_id: jobId },
    );
  },

  trackWeightChanges: ({
    jobId,
    nextWeights,
    previousWeights,
  }: {
    jobId: string;
    nextWeights: SearchV2Weights;
    previousWeights: SearchV2Weights;
  }) => {
    const metadata = {
      ...SEARCH_V2_ANALYTICS_METADATA,
      activity_weight: nextWeights.activityWeight,
      job_id: jobId,
      semantic_weight: nextWeights.semanticWeight,
      text_weight: nextWeights.textWeight,
      we_recency_weight: nextWeights.weRecencyWeight,
    };
    if (previousWeights.semanticWeight !== nextWeights.semanticWeight) {
      mixpanelUtils.trackFilterAndSortOptionSelected(
        MIXPANEL_SCREENS.JOB,
        "Semantic weight",
        String(nextWeights.semanticWeight),
        metadata,
      );
    }
    if (previousWeights.activityWeight !== nextWeights.activityWeight) {
      mixpanelUtils.trackFilterAndSortOptionSelected(
        MIXPANEL_SCREENS.JOB,
        "Activity weight",
        String(nextWeights.activityWeight),
        metadata,
      );
    }
    if (previousWeights.weRecencyWeight !== nextWeights.weRecencyWeight) {
      mixpanelUtils.trackFilterAndSortOptionSelected(
        MIXPANEL_SCREENS.JOB,
        "WE recency weight",
        String(nextWeights.weRecencyWeight),
        metadata,
      );
    }
  },
};